import { makeAutoObservable, runInAction } from 'mobx';
import cartService from '../services/cartService';
import orderService from '../services/orderService';

class CartStore {
  cart = []; // Array of partner groups with items
  loading = false;
  error = null;

  // Orders
  orders = [];
  currentOrder = null;
  ordersLoading = false;

  constructor() {
    makeAutoObservable(this);
  }

  // Computed values
  get totalItems() {
    return this.cart.reduce((total, partner) => total + partner.item_count, 0);
  }

  get cartTotal() {
    return this.cart.reduce((total, partner) => total + parseFloat(partner.total), 0);
  }

  get isEmpty() {
    return this.cart.length === 0;
  }

  // Actions
  async fetchCart() {
    this.loading = true;
    this.error = null;

    try {
      const result = await cartService.getCart();

      runInAction(() => {
        if (result.success) {
          this.cart = result.data;
        } else {
          // Clear cart on error (e.g., user not authenticated)
          this.cart = [];
          this.error = result.error || 'Failed to fetch cart';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        // Clear cart on error
        this.cart = [];
        this.error = error.message || 'Failed to fetch cart';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async addToCart(productVariantId, partnerInventoryId, quantity = 1) {
    this.loading = true;
    this.error = null;

    try {
      const result = await cartService.addToCart({
        product_variant_id: productVariantId,
        partner_inventory_id: partnerInventoryId,
        quantity: quantity
      });

      runInAction(() => {
        if (result.success) {
          // Refresh cart to get updated grouping
          this.fetchCart();
        } else {
          this.error = result.error || 'Failed to add item to cart';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to add item to cart';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async updateCartItem(cartItemId, quantity) {
    this.loading = true;
    this.error = null;

    try {
      const result = await cartService.updateCartItem(cartItemId, { quantity });

      runInAction(() => {
        if (result.success) {
          // Refresh cart to get updated totals
          this.fetchCart();
        } else {
          this.error = result.error || 'Failed to update cart item';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to update cart item';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async removeCartItem(cartItemId) {
    this.loading = true;
    this.error = null;

    try {
      const result = await cartService.removeCartItem(cartItemId);

      runInAction(() => {
        if (result.success) {
          // Refresh cart
          this.fetchCart();
        } else {
          this.error = result.error || 'Failed to remove cart item';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to remove cart item';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async clearCart() {
    this.loading = true;
    this.error = null;

    try {
      const result = await cartService.clearCart();

      runInAction(() => {
        if (result.success) {
          this.cart = [];
        } else {
          this.error = result.error || 'Failed to clear cart';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to clear cart';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async checkout(partnerId, shippingAddress, notes = '', animapNgoId = null, partnerNgoId = null, invoiceData = null, couponCode = null) {
    this.loading = true;
    this.error = null;

    try {
      const checkoutData = {
        partner_id: partnerId,
        shipping_address: shippingAddress,
        notes: notes
      };

      // Add NGO selections if provided
      if (animapNgoId) {
        checkoutData.animap_ngo_id = animapNgoId;
      }
      if (partnerNgoId) {
        checkoutData.partner_ngo_id = partnerNgoId;
      }

      // Add invoice data if provided
      if (invoiceData) {
        checkoutData.wants_invoice = invoiceData.wants_invoice;
        checkoutData.vat_number = invoiceData.vat_number;
        checkoutData.company_name = invoiceData.company_name;
        checkoutData.profession = invoiceData.profession;
        checkoutData.tax_office = invoiceData.tax_office;
      }

      // Add coupon code if provided
      if (couponCode) {
        checkoutData.coupon_code = couponCode;
      }

      const result = await cartService.checkout(checkoutData);

      runInAction(() => {
        if (result.success) {
          this.currentOrder = result.data.order;
          // Refresh cart to remove checked out items
          this.fetchCart();
        } else {
          this.error = result.error || 'Failed to checkout';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to checkout';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Unified checkout - checkout all items at once with single payment
  async unifiedCheckout(shippingAddress, notes = '', animapNgoId = null, partnerNgoId = null, invoiceData = null, couponCode = null) {
    this.loading = true;
    this.error = null;

    try {
      const checkoutData = {
        shipping_address: shippingAddress,
        notes: notes
      };

      // Add NGO selections if provided
      if (animapNgoId) {
        checkoutData.animap_ngo_id = animapNgoId;
      }
      if (partnerNgoId) {
        checkoutData.partner_ngo_id = partnerNgoId;
      }

      // Add invoice data if provided
      if (invoiceData) {
        checkoutData.wants_invoice = invoiceData.wants_invoice;
        checkoutData.vat_number = invoiceData.vat_number;
        checkoutData.company_name = invoiceData.company_name;
        checkoutData.profession = invoiceData.profession;
        checkoutData.tax_office = invoiceData.tax_office;
      }

      // Add coupon code if provided
      if (couponCode) {
        checkoutData.coupon_code = couponCode;
      }

      const result = await cartService.unifiedCheckout(checkoutData);

      runInAction(() => {
        if (result.success) {
          // Store all created orders
          this.currentOrder = result.data.orders;
          // Refresh cart (should be empty now)
          this.fetchCart();
        } else {
          this.error = result.error || 'Failed to checkout';
        }
        this.loading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to checkout';
        this.loading = false;
      });
      return { success: false, error: this.error };
    }
  }

  // Order management
  async fetchOrders() {
    this.ordersLoading = true;
    this.error = null;

    try {
      const result = await orderService.getOrders();

      runInAction(() => {
        if (result.success) {
          this.orders = result.data;
        } else {
          this.error = result.error || 'Failed to fetch orders';
        }
        this.ordersLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch orders';
        this.ordersLoading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async fetchOrderById(orderId) {
    this.ordersLoading = true;
    this.error = null;

    try {
      const result = await orderService.getOrderById(orderId);

      runInAction(() => {
        if (result.success) {
          this.currentOrder = result.data;
        } else {
          this.error = result.error || 'Failed to fetch order';
        }
        this.ordersLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to fetch order';
        this.ordersLoading = false;
      });
      return { success: false, error: this.error };
    }
  }

  async cancelOrder(orderId) {
    this.ordersLoading = true;
    this.error = null;

    try {
      const result = await orderService.cancelOrder(orderId);

      runInAction(() => {
        if (result.success) {
          // Update current order if it's the one being cancelled
          if (this.currentOrder && this.currentOrder.id === orderId) {
            this.currentOrder = result.data.order;
          }
          // Refresh orders list
          this.fetchOrders();
        } else {
          this.error = result.error || 'Failed to cancel order';
        }
        this.ordersLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error.message || 'Failed to cancel order';
        this.ordersLoading = false;
      });
      return { success: false, error: this.error };
    }
  }

  clearCurrentOrder() {
    this.currentOrder = null;
  }

  clearError() {
    this.error = null;
  }

  // Reset cart state (called on logout)
  reset() {
    this.cart = [];
    this.orders = [];
    this.currentOrder = null;
    this.loading = false;
    this.ordersLoading = false;
    this.error = null;
  }
}

const cartStore = new CartStore();
export default cartStore;
